import {h, Fragment} from "preact"
import { SerpElement, useCurrencyFormatting } from "@nosto/preact"
import { useEffect, useState, useMemo } from "preact/hooks";
import { getSrcset, getTotalColors, getProductBadge, productImagePlaceholder, fetchPrice } from "../helpers"

export default function Product({ product, editorialPos }) {
    const [ prices , setPrices ] = useState({ price: product.price, listPrice: product.listPrice })
    const { format } = useCurrencyFormatting();
    const handle = useMemo(() => product.url.split('/products/')[1], []);
    const url = useMemo(() => `${window.Shopify?.routes?.root ?? '/'}products/${handle}`, [])
    const hasAltImage = useMemo(() => product.alternateImageUrls && product.alternateImageUrls.length > 0, []);
    const totalColors = useMemo(() => getTotalColors(product.skus), []);
    const badge = useMemo(() => getProductBadge(product), []);

    useEffect(() => {
        const getPrices = async () => {
            const data = await fetchPrice(handle);
            const highestSkuPrice = data.price_varies ? data.price_max : false;
            const highestPricedSku = data.variants.find(variant => variant.price === highestSkuPrice);
            if((highestSkuPrice / 100) !== product.price && highestPricedSku) {
                setPrices({
                    price: highestPricedSku.price / 100,
                    listPrice: highestPricedSku.compare_at_price ? highestPricedSku.compare_at_price / 100 : 0
                })
            } else {
                setPrices({
                    price: product.price,
                    listPrice: product.listPrice
                })
            }
        }
        getPrices();
    }, [])

    return (
        <SerpElement
            as="li"
            hit={product}
            className={`ns-product w-full m-0 p-0 ${
                    editorialPos === 'first' 
                    ? 'col-start-1 col-end-3 lg:col-start-1 lg:col-end-3 lg:row-start-1 lg:row-end-3' 
                    : editorialPos === 'last' 
                        ? 'col-start-1 col-end-3 lg:col-start-3 lg:col-end-5 lg:row-start-1 lg:row-end-3' 
                        : ''
            }`}
            data-handle={handle}
        >
            <div className="flex flex-col w-full h-full group">
                <div className={`relative rounded overflow-hidden ${!!editorialPos ? 'flex-1 aspect-[924/1244]' : 'aspect-[458/575]'}`}>
                    <a href={url} className="contents ns-prod-link">
                        <img
                            class={`absolute left-0 top-0 w-full h-full object-cover ${hasAltImage ? 'group-hover:hidden' : ''}`}
                            alt={product.name}
                            srcset={product.imageUrl ? getSrcset(product.imageUrl) : productImagePlaceholder}
                            src={product.imageUrl ? `${product.imageUrl}&amp;width=1000` : productImagePlaceholder}
                            width="1000"
                            height="1254"
                            loading="lazy"
                        />
                        {hasAltImage &&
                            <img
                                alt={product.name}
                                srcset={getSrcset(product.alternateImageUrls[product.alternateImageUrls.length - 1])}
                                src={`${product.alternateImageUrls[product.alternateImageUrls.length - 1]}&amp;width=1000`}
                                width="1000"
                                height="1254"
                                loading="lazy"
                                className="absolute left-0 top-0 w-full h-full object-cover hidden group-hover:block"
                            />
                        }
                    </a>
                </div>
                <div className="mt-4">
                    {(badge.text && badge.color && badge.background) && 
                        <div className="mb-2">
                            <div
                                className="ns-badge"
                                style={`color: ${badge.color}; background-color: ${badge.background};`}
                            >
                            {badge.text}
                            </div>
                        </div>
                    }
                    <h4 className="text-sm lg:text-base text-black font-instrumentRegular m-0">
                        <a href={url} className="text-black no-underline ns-prod-link prodName">
                            {product.name}
                        </a>
                    </h4>

                    <div className="flex items-center gap-2 mt-2 text-sm ns-price-wrap">
                        {(typeof prices.listPrice === 'number' && prices.listPrice > prices.price) && (
                            <>
                            <span className="visually-hidden">Regular Price</span>
                            <span className="line-through text-grey-3 ns-reg-price">
                                {format(prices.listPrice, window.Shopify?.currency?.active)}
                            </span>
                            </>
                        )}
                        <span className="visually-hidden">Sale Price</span>
                        <span className="text-black ns-price">
                            {format(prices.price, window.Shopify?.currency?.active)}
                        </span>
                    </div>
                    
                    {totalColors > 1 &&
                        <div className="text-xs lg:text-sm text-grey-3 mt-2">
                            {totalColors} colors
                        </div>
                    }
                </div>
            </div>
        </SerpElement>
    )
}
